/**
 * Retry wrapper for dynamic imports (React.lazy).
 *
 * When a dynamic import fails (typically due to stale assets after a deploy),
 * this retries the import once after a brief delay. If the retry also fails,
 * the error propagates to the nearest error boundary.
 *
 * Usage:
 *   const MyPage = lazy(() => retryDynamicImport(() => import('./pages/MyPage')));
 */
export function retryDynamicImport<T>(
  importFn: () => Promise<T>,
  retries = 1,
  delay = 1000,
): Promise<T> {
  return importFn().catch((error: unknown) => {
    if (retries <= 0) throw error;
    return new Promise<T>((resolve) =>
      setTimeout(() => resolve(retryDynamicImport(importFn, retries - 1, delay)), delay)
    );
  });
}
